"use client"

import { useCallback, useRef, useState } from "react"
import { Controller, FieldPath, FieldValues, useFormContext } from "react-hook-form"
import { TEditorIconButton } from "../../ui/button/TEditorIconButton"
import { FormControl, FormItem, FormMessage } from "../../ui/shadcn/form"
import { DynamicFormLabel } from "../components/form-label"
import type { ParsedField } from "../types"
import { TTooltip } from "../../ui/tooltip/TTooltip"

const SIGNED_32_BIT_INTEGER_MAX = 2_147_483_647

interface RandomNumberFieldProps {
	field: ParsedField
	minValue?: number
	maxValue?: number
	useComma?: boolean
	/** input placeholder — 소비 앱에서 i18n 번역값을 주입한다 (미지정 시 기존 "random") */
	placeholder?: string
	/** 랜덤 생성 버튼 hover 툴팁 — 소비 앱에서 i18n 번역값을 주입한다 (미지정 시 기존 "Random") */
	randomTooltip?: string
}

export function RandomNumberField({
	field: parsedField,
	minValue = 0,
	maxValue = SIGNED_32_BIT_INTEGER_MAX,
	useComma = true,
	placeholder = "random",
	randomTooltip = "Random"
}: RandomNumberFieldProps) {
	const { name, schema, required } = parsedField
	const description = schema.description
	const title = schema.title
	const { control } = useFormContext()
	const inputRef = useRef<HTMLInputElement>(null)
	const [inputValue, setInputValue] = useState<string>("")
	const lastFieldValueRef = useRef<number | undefined>(undefined)

	const min = (schema.minimum as number) ?? minValue
	const max = (schema.maximum as number) ?? maxValue
	const maxLength = String(max).length

	const formatNumber = useCallback((num: number): string => {
		return num.toLocaleString()
	}, [])

	const removeNonDigits = useCallback((str: string): string => {
		return str.replace(/\D/g, "")
	}, [])

	const clampValue = useCallback(
		(value: number): number => {
			let result = Math.round(value)
			result = Math.max(min, result)
			result = Math.min(max, result)
			return result
		},
		[min, max]
	)

	const generateRandomNumber = useCallback(() => {
		return Math.floor(Math.random() * (max - min + 1)) + min
	}, [min, max])

	return (
		<Controller
			name={name as FieldPath<FieldValues>}
			control={control}
			render={({ field, fieldState }) => {
				// field.value가 외부에서 변경되었을 때만 inputValue 동기화
				if (field.value !== lastFieldValueRef.current) {
					lastFieldValueRef.current = field.value
					if (typeof field.value === "number") {
						setInputValue(useComma ? formatNumber(field.value) : String(field.value))
					} else if (field.value === undefined) {
						setInputValue("")
					}
				}

				const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
					const rawValue = e.target.value
					const cleanedValue = removeNonDigits(rawValue)

					// 빈 값 허용
					if (cleanedValue === "") {
						setInputValue("")
						lastFieldValueRef.current = undefined
						field.onChange(undefined)
						return
					}

					// 숫자만 허용
					if (!/^\d*$/.test(cleanedValue)) return

					// max 자릿수 초과 방지
					if (cleanedValue.length > maxLength) return

					const num = parseInt(cleanedValue, 10)
					if (isNaN(num)) return

					// 입력 중에는 comma 없이 표시
					setInputValue(cleanedValue)
					lastFieldValueRef.current = num
					field.onChange(num)
				}

				const handleBlur = () => {
					if (typeof field.value === "number") {
						const clamped = clampValue(field.value)
						lastFieldValueRef.current = clamped
						field.onChange(clamped)
						setInputValue(useComma ? formatNumber(clamped) : String(clamped))
					}
				}

				const handleRandomClick = () => {
					const randomNum = generateRandomNumber()
					lastFieldValueRef.current = randomNum
					field.onChange(randomNum)
					setInputValue(useComma ? formatNumber(randomNum) : String(randomNum))
				}

				const handleFormItemClick = () => {
					inputRef.current?.focus()
				}

				return (
					<FormItem
						className="bg-g800/10 rounded-lg px-3 py-2 flex col group transition-colors cursor-text hover:bg-g800/20 focus-within:bg-g800/20"
						onClick={handleFormItemClick}
					>
						<div className="flex flex-1 items-center">
							<div className="flex-1 flex flex-col gap-1">
								<DynamicFormLabel name={name} title={title} required={required} description={description} />
								<FormControl>
									<input
										ref={inputRef}
										type="text"
										inputMode="numeric"
										name={name}
										value={inputValue}
										onChange={handleChange}
										onBlur={handleBlur}
										onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
										placeholder={placeholder}
										autoComplete="off"
										className="w-full bg-transparent outline-none text-white placeholder:text-text-placeholder"
										aria-invalid={!!fieldState.error}
									/>
								</FormControl>
							</div>
							<TTooltip title={randomTooltip} side="bottom" sideOffset={-6}>
								<TEditorIconButton name="shuffle" size="small" color="ghost" onClick={handleRandomClick} className="self-center" />
							</TTooltip>
						</div>
						<FormMessage />
					</FormItem>
				)
			}}
		/>
	)
}
